// React mount + Polaris AppProvider.
// App Bridge auto-initializes from the shopify-api-key meta tag in
// index.html when the SPA is embedded in Shopify Admin or the storefront
// proxy surface. Outside the embed (local dev, direct browser hit), the
// window.shopify object is unavailable and the auth hooks render a
// "Please open from your Shopify Admin" surface.

import { AppProvider } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import enTranslations from '@shopify/polaris/locales/en.json';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { SessionProvider } from './hooks/SessionProvider';
import { initSentryWeb } from './lib/sentry';
import './styles/reset.css';
import './styles/tokens.css';
import './styles/senior.css';
import './styles/shell.css';
import './styles/chat.css';
import './styles/polish.css';

initSentryWeb();

// Register the service worker so the Web Push channel can wake the
// app when a reminder fires. Permission is requested separately from
// the Settings page — registration is harmless without permission.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).catch((err) => {
      // Service-worker registration only fails when the script
      // 404s (dev without the public dir) or HTTPS is off. Quiet log
      // so it doesn't spam the console.
      // eslint-disable-next-line no-console
      console.debug('service worker registration failed', err);
    });
  });
}

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <AppProvider i18n={enTranslations}>
      <SessionProvider>
        <App />
      </SessionProvider>
    </AppProvider>
  </React.StrictMode>,
);
